/* 

Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.

*/

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import { useAuthContext } from '../../contexts/AuthContext';
import { useLoanContext } from '../../contexts/LoanContext';
import axiosCall from '../../services/axios';
import useAbortController from './useAbortController';

type UseLoanPayoff = {
  loanPayoffBalance: LoanPayoffBalance;
  loadingLoanPayoffBalance: boolean;
  reloadLoanPayoffBalance: () => void;
  pullLoanPayoff: (
    fields: Record<string, any>,
    path: string,
    setErrors: Dispatch<SetStateAction<Record<string, any>>>,
  ) => Promise<LoanPayoffBalance>;
};

const useLoanPayoff = (pathName = ``): UseLoanPayoff => {
  const PAYOFF_INSUFFICIENT_FUNDS = 'Insufficient funds to payoff loan.';
  const queryClient = useQueryClient();
  const { get } = useAbortController();
  const { token } = useAuthContext();
  const { loanNumber } = useLoanContext();
  const path = useMemo(() => {
    if (!pathName) {
      const year = new Date().getFullYear();
      const month = `${new Date().getMonth() + 1}`.padStart(2, '0');
      const day = `${new Date().getDate()}`.padStart(2, '0');
      return `/${year}${month}${day}`;
    }
    return '/' + pathName;
  }, [pathName]);
  const queryKey = useMemo(() => ['loanPayoffBalance', path, token], [path, token]);

  // Fetch communication data using react-query
  const { data, isLoading } = useQuery({
    queryKey: queryKey,
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      if (!loanNumber || !path || !token) return null;
      try {
        const request = {
          url: process.env.REACT_APP_API_PAYOFF.replace('?', loanNumber).replace('?', path),
          method: 'POST',
          data: { noPayoffLetterEvent: true },
        };
        const config = {
          signal,
        };
        const headers = {
          'Session-Token': token,
        };
        const response = await axiosCall(request, config, headers);
        console.log(`useLoanPayoff.response: `, response);
        return { data: response };
      } catch (error) {
        console.error(`useLoanPayoff.caught: ${error.message}`);
        return {
          data: null,
        };
      }
    },
  });

  const reloadLoanPayoffBalance = useCallback(
    () => queryClient.invalidateQueries({ queryKey }),
    [queryClient, queryKey],
  );

  const pullLoanPayoff = useCallback(
    async (fields, path, setErrors) => {
      if (!fields || typeof fields !== 'object') {
        throw new Error('!fields');
      }
      if (!path) {
        throw new Error('!path');
      }
      const errors = {
        generic: '',
      };
      setErrors((prev) => ({ ...prev, ...errors }));
      if (Object.values(errors).filter((v) => v).length > 0)
        return {
          data: null,
        };

      try {
        const request = {
          url: process.env.REACT_APP_API_PAYOFF.replace('?', loanNumber).replace('?', path),
          method: 'POST',
          data: fields,
        };
        const config = get();
        const headers = {
          'Session-Token': token,
        };
        const response = await axiosCall(request, config, headers);
        console.log(`useLoanPayoff pullLoanPayoff.response: `, response);

        // add minimum payment amount in error message for insufficient funds
        if (response?.errors === PAYOFF_INSUFFICIENT_FUNDS) {
          const usdFormatter = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
          });
          const minimumPaymentAmount = usdFormatter.format(response?.payoff - response?.escrowBal);
          setErrors({ generic: `${response.errors} Minimum payment amount: ${minimumPaymentAmount}.` });
        } else {
          setErrors({ generic: response?.errors });
        }
        return response;
      } catch (error) {
        console.error(`useLoanPayoff pullLoanPayoff.caught: ${error.message}`);
        return {
          data: null,
        };
      }
    },
    [loanNumber, get, token],
  );

  return {
    loanPayoffBalance: !isLoading && data && 'data' in data && data.data ? data.data : null,
    loadingLoanPayoffBalance: isLoading,
    reloadLoanPayoffBalance,
    pullLoanPayoff,
  };
};

export default useLoanPayoff;
